import { useState } from "react";
import { Link as ReactRouterLink } from "react-router-dom";
import {
  LayoutGrid,
  Building2,
  BarChart3,
  Timer,
  Globe,
  Users,
  ArrowRight,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import useTranslation from "../../client/hooks/useTranslation";

/**
 * "Six outils. Un seul hub." — interactive tabbed showcase on the homepage.
 *
 * Deliberate design exception: this section is a FIXED dark premium showcase
 * regardless of the active theme, the same way social-reports pins its own
 * palette. To stay visually consistent with the in-app dark UI ("Dark Terminal
 * Pro"), it reuses the real app surface hexes rather than arbitrary
 * white-opacity greys — see app/src/client/Main.css `.dark`:
 *   bg #080D1A · card #0F1829 · card-accent #162035 · teal #00C9A7.
 * Copy still flows through i18n (t.marketing.featureTabs); only the per-tab
 * icon, route and image live here.
 */

// App dark-terminal surfaces (mirror of Main.css `.dark` tokens).
const SURFACE = {
  bg: "#080D1A", // --background
  card: "#0F1829", // --card
  cardAccent: "#162035", // --card-accent
  border: "rgba(125, 152, 191, 0.18)", // --border (slate-teal subtle)
  borderStrong: "rgba(125, 152, 191, 0.28)",
  teal: "#00C9A7", // --edgely-teal-muted (the brand teal used app-wide)
  muted: "rgba(226, 232, 240, 0.55)", // ~ --muted-foreground on dark
};

// Static per-tab metadata. Order MUST match t.marketing.featureTabs.tabs.
const TAB_META: { key: string; icon: LucideIcon; to: string; img: string }[] = [
  { key: "dashboard", icon: LayoutGrid, to: "/fonctionnalites/dashboard-trading", img: "/images/features/dashboard.png" },
  { key: "propFirmManager", icon: Building2, to: "/fonctionnalites/prop-firm-manager", img: "/images/features/prop-firm-manager.png" },
  { key: "analytics", icon: BarChart3, to: "/fonctionnalites/analytics-trading", img: "/images/features/analytics.png" },
  { key: "sessions", icon: Timer, to: "/fonctionnalites/sessions-de-trading", img: "/images/features/sessions.png" },
  { key: "marketData", icon: Globe, to: "/fonctionnalites/dashboard-trading", img: "/images/features/market-data-pro.png" },
  { key: "mentor", icon: Users, to: "/fonctionnalites/mode-mentor", img: "/images/features/mode-mentor.png" },
];

export default function FeatureTabs() {
  const t = useTranslation();
  const ft = t.marketing.featureTabs;
  const [active, setActive] = useState(0);

  const meta = TAB_META[active];
  const tab = ft.tabs[active];

  return (
    <section id="features" className="relative overflow-x-clip py-24 sm:py-32 mx-[calc(-50vw+50%)] w-screen">
      {/* Section fill: app dark bg (#080D1A). The fill layer DELIBERATELY bleeds
          ~14rem past the section top & bottom (-inset-y) so its transparent-edged
          gradient overlaps the neighbouring sections — the dark wash fades in over
          the previous section and out into the next one, with no hard demarcation
          line. overflow-x-clip keeps the horizontal bleed (w-screen) in check
          while allowing the vertical bleed through. */}
      <div
        className="pointer-events-none absolute inset-x-0 -top-56 -bottom-56"
        style={{
          background: `linear-gradient(to bottom, transparent 0%, ${SURFACE.bg} 30%, ${SURFACE.bg} 70%, transparent 100%)`,
        }}
      />
      {/* HUD grid overlay — bleeds with the fill so the grid never reveals a hard
          edge, masked to fade out where the dark wash fades. */}
      <div
        className="pointer-events-none absolute inset-x-0 -top-56 -bottom-56 opacity-30"
        style={{
          backgroundImage: `
            linear-gradient(rgba(0,201,167,0.04) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0,201,167,0.04) 1px, transparent 1px)
          `,
          backgroundSize: "44px 44px",
          WebkitMaskImage:
            "linear-gradient(to bottom, transparent 0%, #000 30%, #000 70%, transparent 100%)",
          maskImage:
            "linear-gradient(to bottom, transparent 0%, #000 30%, #000 70%, transparent 100%)",
        }}
      />
      {/* Ambient glow */}
      <div
        className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 h-72 w-[800px] rounded-full blur-3xl"
        style={{ backgroundColor: "rgba(0,201,167,0.08)" }}
      />

      <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-8">
        {/* Header */}
        <div className="text-center">
          <span
            className="text-xs font-semibold uppercase"
            style={{ color: "#00C9A7", letterSpacing: "0.18em" }}
          >
            {ft.eyebrow}
          </span>
          <h2
            className="mt-3 text-3xl font-bold tracking-tight sm:text-4xl md:text-5xl"
            style={{ color: "#ffffff", fontFamily: "'Space Grotesk', sans-serif" }}
          >
            {ft.title}
          </h2>
          <p
            className="mx-auto mt-4 max-w-2xl text-base sm:text-lg"
            style={{ color: "rgba(255,255,255,0.6)", lineHeight: 1.6 }}
          >
            {ft.subtitle}
          </p>
        </div>

        {/* Tabs row — horizontal scroll on mobile, no visible scrollbar */}
        <div
          role="tablist"
          aria-label={ft.title}
          className="no-scrollbar mt-10 flex justify-start gap-2 overflow-x-auto pb-1 sm:justify-center sm:flex-wrap"
        >
          {ft.tabs.map((tb, i) => {
            const Icon = TAB_META[i].icon;
            const isActive = i === active;
            return (
              <button
                key={TAB_META[i].key}
                role="tab"
                id={`feature-tab-${TAB_META[i].key}`}
                aria-selected={isActive}
                aria-controls={`feature-panel-${TAB_META[i].key}`}
                tabIndex={isActive ? 0 : -1}
                onClick={() => setActive(i)}
                className="flex shrink-0 items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors duration-150"
                style={{
                  backgroundColor: isActive ? "rgba(0,201,167,0.12)" : SURFACE.card,
                  color: isActive ? SURFACE.teal : SURFACE.muted,
                  border: `1px solid ${isActive ? SURFACE.teal : SURFACE.border}`,
                }}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.backgroundColor = SURFACE.cardAccent;
                    e.currentTarget.style.borderColor = SURFACE.borderStrong;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.backgroundColor = SURFACE.card;
                    e.currentTarget.style.borderColor = SURFACE.border;
                  }
                }}
              >
                <Icon className="h-4 w-4" />
                <span className="whitespace-nowrap">{tb.tabLabel}</span>
              </button>
            );
          })}
        </div>

        {/* Content card */}
        <div
          key={meta.key}
          role="tabpanel"
          id={`feature-panel-${meta.key}`}
          aria-labelledby={`feature-tab-${meta.key}`}
          className="feature-tab-fade mt-10 grid grid-cols-1 items-center gap-8 rounded-2xl p-6 sm:p-12 lg:grid-cols-[40%_60%] lg:gap-12 lg:p-14"
          style={{
            backgroundColor: SURFACE.card,
            border: `1px solid ${SURFACE.border}`,
            boxShadow: "0 1px 0 rgba(255,255,255,0.03) inset",
          }}
        >
          {/* Image (top on mobile, right on desktop) */}
          <div className="order-1 flex items-center justify-center lg:order-2">
            <img
              src={meta.img}
              alt={tab.h1}
              loading={active === 0 ? "eager" : "lazy"}
              className="w-full lg:-translate-y-2"
              style={{
                objectFit: "contain",
                borderRadius: "12px",
                boxShadow: "0 24px 64px rgba(0,0,0,0.45)",
              }}
            />
          </div>

          {/* Text (bottom on mobile, left on desktop) */}
          <div className="order-2 flex flex-col justify-center lg:order-1">
            <span
              className="text-[11px] font-semibold uppercase"
              style={{ color: "#00C9A7", letterSpacing: "0.1em" }}
            >
              {tab.sectionLabel}
            </span>
            <h3
              className="mt-3 text-2xl font-bold sm:text-3xl"
              style={{ color: "#ffffff", fontFamily: "'Space Grotesk', sans-serif" }}
            >
              {tab.h1}
            </h3>
            <p
              className="mt-4 text-base"
              style={{ color: "rgba(255,255,255,0.8)", lineHeight: 1.6 }}
            >
              {tab.description}
            </p>
            <ul className="mt-6 space-y-3">
              {tab.bullets.map((b) => (
                <li key={b} className="flex items-start gap-3">
                  <span
                    className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full"
                    style={{ backgroundColor: "#00C9A7" }}
                  />
                  <span className="text-[15px]" style={{ color: "#ffffff" }}>
                    {b}
                  </span>
                </li>
              ))}
            </ul>
            <ReactRouterLink
              to={meta.to}
              className="group mt-8 inline-flex items-center gap-2 text-sm font-medium"
              style={{ color: "#ffffff" }}
            >
              <span className="group-hover:underline">{tab.linkLabel}</span>
              <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
            </ReactRouterLink>
          </div>
        </div>
      </div>
    </section>
  );
}
